// Visitor & Contractor module — wire-format mapper (RON-5466 / RON-6002).
//
// The real API speaks snake_case with nested objects; every screen in this module reads a flat
// camelCase row. This file is the only place that knows both, so the swap in `VisitorAPI.jsx`
// changes three lines per function and NO component moves.
//
// Contract (READ-ONLY — the Laravel team's folder, never write to it):
//   D:\laragon\www\laravel-migration\docs\RON-5460\RON-6002\RON-6002_api-employee-invite.md
// The fixtures in the unit tests are copied verbatim out of that document, so a change on their
// side fails a test here rather than a screen in QA.
//
// ── Fields the screens read that the published contract does not list ────────────────────────
// Tracked, with the Laravel team's answers as they arrive, in `docs/RON-6002_open-questions.md`.
// Each is mapped defensively — read if present, `undefined` if not — so an answer costs a test
// fixture rather than a rewrite.
//
//   activity     `GET /visitor/audit` needs `vcm.audit.view`, granted to `vcm_admin` only — open
//   host         not returned — `/visitor/me/visits` is scoped to the caller, who IS the host
//
// REMOVED 07-October-2026 by product decision: `expectedOut` and `itProvided` are gone from the
// invite flows. `itProvided` never had a backing column; `expected_out` has one and Reception
// still uses it, so the COLUMN stays — only these screens stop reading and sending it.
//
// ── Two shapes that are correct and easy to misread ──────────────────────────────────────────
//   `group_id` is null for a solo invite  — a party of one is not a group
//   `group`    is everyone ELSE on the booking, and [] for a solo invite

// ── Scalars ──────────────────────────────────────────────────────────────────────────────────

// The API takes `HH:MM` and returns `HH:MM:SS` (§4.5 vs §4.6). Every screen renders and edits
// `HH:MM`, so normalise on the way in rather than in six components.
//
// A DATE PREFIX IS TOLERATED. `expected_out` is stored server-side as a full datetime — reception
// writes it that way, and the Laravel team confirmed they combine our `HH:MM` with the visit date
// on their side (Q1). They say it reads back like `arrival_time`, but accepting both costs one
// line and is the difference between "17:00" and "2026-10-01 17" in front of a user.
//
// Anything that is not recognisably a time returns `undefined` rather than being passed through —
// a half-parsed value rendered into a time field looks set when it is not.
const TIME_ONLY = /^\d{1,2}:\d{2}$/;

export const timeFromApi = (value) => {
  if (typeof value !== "string" || value === "") return undefined;

  // "2026-10-01T17:00:00" and "2026-10-01 17:00:00" both reduce to the time half.
  const withoutDate = value.includes("T") ? value.split("T")[1] : value.split(" ").pop();
  const [hours, minutes] = String(withoutDate).split(":");
  const time = minutes === undefined ? withoutDate : `${hours}:${minutes}`;

  return TIME_ONLY.test(time) ? time : undefined;
};

// And back out. Tolerates a value that already carries seconds so a round-trip is safe.
export const timeToApi = (value) => timeFromApi(value);

// Only keys the caller actually set. A PATCH is partial (§4.7) and a bulk edit sends two or three
// fields — sending `null` for the rest would blank them.
const omitUndefined = (object) =>
  Object.entries(object).reduce((kept, [key, value]) => {
    if (value !== undefined) kept[key] = value;
    return kept;
  }, {});

// ── Idempotency (§6) ─────────────────────────────────────────────────────────────────────────
//
// Required on endpoints 6, 7, 8 and 9. One key per user ACTION, reused on retry — so it is
// generated where the action starts (a wizard submit, a cancel confirm), not per request. A bulk
// operation that fans out to N requests therefore needs N keys, one per row, because each row is
// its own action server-side.
//
// `crypto.randomUUID` is unavailable on http:// origins in some browsers and in older jsdom, so
// there is a fallback. It only has to be unique per user action, not cryptographically strong.
export const newIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;

    return value.toString(16);
  });
};

// ── Reads ────────────────────────────────────────────────────────────────────────────────────

// One audit row from the visit's embedded trail. The module renders `{ event, at, by }`.
// SHIPPED 16-October-2026 (answers-to-frontend-9), after being promised on 23-September and not
// built. One spelling per key, confirmed: `action`, `at`, `at_local`, `actor`.
//
// The alternatives this used to read — `event`, `timestamp`, `by` — are gone. They existed
// because the original answer described the fields in prose rather than showing a payload, and
// reading both spellings was the honest way to wait. There is a payload now.
//
// `at_local` is the VISIT's own site clock, following the `_local` rule. Preferred over `at`
// for the same reason as everywhere else: the row belongs to a site and only the server knows
// which zone that is.
const activityFromApi = (row = {}) => ({
  event: row.action,
  at: row.at_local ?? row.at,
  // `null` when that person no longer exists, which the screen renders as no name rather than
  // an empty separator.
  by: row.actor ?? null,
});

// One visit row (§4.5). Also the element shape inside `POST /visits` → `Records.visits`.
export const visitFromApi = (api) => {
  if (!api) return null;

  const visitor = api.visitor || {};
  const site = api.site || {};
  const building = api.building || {};

  return {
    id: api.id,
    // The module carries both; the API's `id` is already the guid.
    guid: api.id,
    bookingCode: api.booking_code,
    kind: api.kind,
    type: api.type,
    status: api.status,

    name: visitor.name,
    email: visitor.email,
    company: visitor.company,

    purpose: api.purpose,
    startDate: api.start_date,
    // §4.6: `end_date` defaults to `start_date`. Doing it here means no screen has to.
    endDate: api.end_date ?? api.start_date,
    arrivalTime: timeFromApi(api.arrival_time),

    // Step 3's five, shipped by Laravel 14-October-2026. On a visitor's visit all five come
    // back `null` — the keys are always there, so there is nothing to branch on.
    workDescription: api.work_description ?? null,
    areas: api.areas ?? null,
    duration: api.duration ?? null,
    personnel: api.personnel ?? null,
    supervisor: api.supervisor ?? null,

    // Screens render the names; the wizard needs the ids to send them back.
    site: site.name,
    siteId: site.id,
    siteTimezone: site.timezone,
    building: building.name,
    buildingId: building.id,

    isProvisional: api.is_provisional,
    group: api.group_id ?? null,

    // §4.5: "the server's answer. Use them." Never re-derived from `status`.
    canEdit: api.can_edit,
    canCancel: api.can_cancel,

    // Everyone else on the booking. Kept as `party` so it cannot be confused with `group`,
    // which is the id.
    party: Array.isArray(api.group)
      ? api.group.map((member) => ({
          id: member.id,
          name: member.name,
          status: member.status,
          type: member.type,
        }))
      : [],

    host: api.host?.name,

    // Q4, answered 23-Sep-2026: a short trail rides on `GET /visits/{id}` — action, timestamp and
    // the actor's display name, newest first, capped (~10). NOT the before/after payloads the
    // admin audit holds; those can contain other people's data.
    //
    // TREAT AS POSSIBLY EMPTY. An invite created before that ships has no rows, and the list read
    // (§4.4) carries no trail at all — only the single-visit read does. So `activity` is absent
    // rather than `[]` when the payload never had one: the View screen must be able to tell
    // "nothing happened yet" from "this row didn't come with a trail".
    //
    // ALWAYS PRESENT on the single read as of 16-October-2026 — `[]` rather than absent when
    // there is nothing. The distinction this used to preserve ("nothing happened" versus "this
    // read carries no trail") is gone, and that is a simplification rather than a loss: list rows
    // still carry none, and `undefined` there renders exactly as `[]` does.
    activity: Array.isArray(api.activity) ? api.activity.map(activityFromApi) : undefined,
  };
};

// ── The visits query (§4.4) ──────────────────────────────────────────────────────────────────
//
// `status` must be one of these five. The contract is blunt about why it matters:
//
//   "A misspelled one is a 422, not an empty list — an empty list on screen reads as
//    'you have no visitors' when the truth is a typo."
//
// A 422 is the good outcome. The bad one is a filter that silently returns nothing and a host who
// concludes their visitors were never invited. So the value is checked HERE, before the request
// leaves, and a bad one throws in development rather than reaching the server.
export const VISIT_STATUS_FILTERS = [
  "expected",
  "checked_in",
  "checked_out",
  "cancelled",
  "no_show",
];

export const visitsQueryToApi = ({ Query, Status, Page, PerPage } = {}) => {
  if (Status != null && Status !== "" && !VISIT_STATUS_FILTERS.includes(Status)) {
    // Thrown, not silently dropped: dropping it would show an unfiltered list that looks filtered,
    // which is the same class of lie as the empty list the contract warns about.
    throw new Error(
      `Unknown visit status filter "${Status}". Use one of: ${VISIT_STATUS_FILTERS.join(", ")}.`
    );
  }

  return omitUndefined({
    q: Query || undefined,
    status: Status || undefined,
    page: Page ?? undefined,
    per_page: PerPage ?? undefined,
  });
};

// `GET /visitor/me/visits` → `Records` (§4.4). Paginated: the list screen must carry the cursor.
export const visitListFromApi = (records = {}) => ({
  visits: (records.visits || []).map(visitFromApi),
  total: records.total ?? 0,
  page: records.page ?? 1,
  perPage: records.per_page ?? 0,
});

// `GET /visitor/me/sites` → one site (§4.2).
export const siteFromApi = (api) => {
  if (!api) return null;

  return {
    id: api.id,
    name: api.name,
    address: api.address,
    timezone: api.timezone,
    // §4.2: "a pair or null — never half a coordinate."
    lat: api.location?.lat ?? null,
    lng: api.location?.lng ?? null,
    // Sent so the picker rule is a comparison rather than an array length we have to trust.
    buildingCount: api.building_count ?? (api.buildings || []).length,
    buildings: (api.buildings || []).map((building) => ({
      id: building.id,
      name: building.name,
      isDefault: building.is_default === true,
      // A campus building may carry its own point; fall back to the site's when null.
      lat: building.location?.lat ?? api.location?.lat ?? null,
      lng: building.location?.lng ?? api.location?.lng ?? null,
    })),
  };
};

export const siteListFromApi = (records = {}) =>
  (records.sites || []).map(siteFromApi);

// `GET /visitor/me/host-permissions` → `Records` (§4.1).
export const hostPermissionsFromApi = (records = {}) => {
  const capabilities = records.capabilities || {};

  return {
    canVisitor: capabilities.visitors === true,
    canContractor: capabilities.contractors === true,
    canParking: capabilities.parking === true,
    canDesk: capabilities.desks === true,
    canReception: capabilities.reception === true,
    // §4.1: "Use it rather than reading the map yourself."
    canInvite: records.can_invite === true,
  };
};

// `GET /visitor/me/contractors` → one register row (§4.3).
//
// `vetting.state` is one of started | waiting | review | vetted | expired. It is carried through
// RAW — the module's own three-way enum is being replaced by these five, and translating here
// would hide which value actually came back.
//
// This read carries no documents and no vetting history, by design, so `documents` is absent
// rather than empty: a screen must not render "no documents" from a payload that never had them.
export const contractorFromApi = (api) => {
  if (!api) return null;

  return {
    id: api.id,
    name: api.name,
    email: api.email,
    company: api.company,
    trade: api.trade,
    vettingState: api.vetting?.state,
    vettedUntil: api.vetting?.until ?? null,
  };
};

export const contractorListFromApi = (records = {}) => ({
  contractors: (records.contractors || []).map(contractorFromApi),
  total: records.total ?? 0,
  page: records.page ?? 1,
  perPage: records.per_page ?? 0,
});

// `POST /visitor/me/visits` → `Records` (§4.6), and the contractor gate's answer (§5).
export const createResultFromApi = (records = {}) => ({
  groupId: records.group_id ?? null,
  visits: (records.visits || []).map(visitFromApi),
  // §5: "Show the warning." A provisional contractor IS booked and the host has to know
  // reception may still turn them away.
  warning: records.warning,
});

// `POST /visitor/me/visit-groups/{group_id}/cancel` → `Records` (§4.9).
// Partial by design — "2 cancelled, 1 already checked in" rather than a bare success.
export const groupCancelFromApi = (records = {}) => ({
  cancelled: records.cancelled ?? 0,
  skipped: records.skipped ?? 0,
});

// ── Writes ───────────────────────────────────────────────────────────────────────────────────

// The module's existing request vocabulary (`Kind`, `Visitors`, `SiteID`, …) in, the contract's
// body out. Keeping this translation here is what lets every call site in `VisitorUI.jsx` stay
// exactly as written.
export const createVisitToApi = (params = {}) => {
  const visitors = (Array.isArray(params.Visitors) ? params.Visitors : [])
    .map((visitor) =>
      omitUndefined({
        name: visitor.name,
        // §4.6: each needs `name`; `email` and `company` are optional. An empty string is not a
        // value — the wizard initialises its fields to "" and an untouched optional must not be
        // sent as blank.
        email: visitor.email || undefined,
        company: visitor.company || undefined,
      })
    );

  return omitUndefined({
    kind: params.Kind,
    site_id: params.SiteID ?? undefined,
    building_id: params.BuildingID ?? undefined,
    start_date: params.StartDate,
    end_date: params.EndDate,
    arrival_time: timeToApi(params.ArrivalTime),
    type: params.Type,
    purpose: params.Purpose,
    // `SendEmail` is the module's 1/0; the contract wants a boolean and defaults it to true.
    send_confirmation:
      params.SendEmail === undefined ? undefined : Boolean(Number(params.SendEmail)),
    visitors,

    // Contractor only. The server computes the three-way gate from `contractor_id` alone (§5) —
    // `IsProvisional` / `VettedUntil` are deliberately NOT sent: they are the answer, not the
    // question, and sending our guess is how the two drift.
    contractor_id: params.ContractorID ?? undefined,

    // Step 3's five. `duration` and `supervisor` joined the other three on 14-October-2026 —
    // `duration`'s column had been in the schema all along with nothing writing to it, which is
    // why it looked missing; `supervisor_name` is new.
    //
    // Same names in and out: what goes up on POST is what comes back on the read.
    work_description: params.WorkDescription,
    areas: params.Areas,
    duration: params.Duration,
    personnel: params.Personnel,
    supervisor: params.Supervisor,
  });
};

// `PATCH /visitor/me/visits/{id}` (§4.7). Partial — only what changed.
//
// Not changeable: `kind`, `status`, the host. `HostName` is dropped rather than sent and refused.
export const patchVisitToApi = (params = {}) => {
  const body = omitUndefined({
    visitor_name: params.Name,
    visitor_email: params.Email,
    visitor_company: params.Company,
    purpose: params.Purpose,
    type: params.Type,
    arrival_time: timeToApi(params.ArrivalTime),
    start_date: params.StartDate,
    end_date: params.EndDate,
    site_id: params.SiteID,
    building_id: params.BuildingID,
    // Contractor only. Sending any of these on a VISITOR's visit is a 422 — refused rather than
    // silently dropped, so they cannot be stored somewhere nothing will ever show them.
    work_description: params.WorkDescription,
    areas: params.Areas,
    duration: params.Duration,
    personnel: params.Personnel,
    supervisor: params.Supervisor,
  });

  // §4.7: "Changing site_id clears the building — it belonged to the site you left." Send the new
  // building in the same request or the visit ends up with none.
  if (body.site_id !== undefined && body.building_id === undefined) {
    body.building_id = params.BuildingID ?? null;
  }

  return body;
};
